// @ts-nocheck

import React from 'react';
import {
  Autocomplete as MuiAutocomplete,
  TextField,
  AutocompleteProps as MuiAutocompleteProps,
  TextFieldProps,
  CircularProgress,
  Popper,
  FilterOptionsState,
  createFilterOptions,
  Chip,
} from '@mui/material';
import styles from '@/components/atoms/Autocomplete/autocomplete.module.css';

export interface AutocompleteOption {
  label: string;
  value: string | number;
  group?: string;
  disabled?: boolean;
  [key: string]: any;
}

interface AutocompleteProps
  extends Omit<MuiAutocompleteProps<AutocompleteOption, boolean, boolean, boolean>, 'renderInput'> {
  label?: string;
  error?: boolean;
  helperText?: string;
  required?: boolean;
  loading?: boolean;
  size?: 'small' | 'medium';
  textFieldProps?: Partial<TextFieldProps>;
  customFilter?: (
    options: AutocompleteOption[],
    state: FilterOptionsState<AutocompleteOption>
  ) => AutocompleteOption[];
  popperClassName?: string;
  allowCustomValues?: boolean;
  customValueLabel?: string;
  className?: string;
  renderTags?: (value: AutocompleteOption[], getTagProps: any) => React.ReactNode;
  groupBy?: (option: AutocompleteOption) => string;
  placeholderText?: string;
}

const AtomAutocomplete: React.FC<AutocompleteProps> = ({
  label,
  error = false,
  helperText,
  required = false,
  loading = false,
  size = 'medium',
  textFieldProps,
  customFilter,
  popperClassName = '',
  allowCustomValues = false,
  customValueLabel = 'Add',
  className = '',
  renderTags,
  groupBy,
  placeholderText,
  multiple = false,
  options: providedOptions,
  ...autocompleteProps
}) => {
  // Custom filter configuration
  const filterOptions = createFilterOptions<AutocompleteOption>({
    matchFrom: 'any',
    stringify: option => `${option.label} ${option.value}`,
  });

  const defaultFilterOptions = (
    options: AutocompleteOption[],
    state: FilterOptionsState<AutocompleteOption>
  ) => {
    const filtered = filterOptions(options, state);

    if (allowCustomValues && state.inputValue !== '') {
      const isExisting = options.some(
        option => option.label.toLowerCase() === state.inputValue.toLowerCase()
      );

      if (!isExisting) {
        filtered.push({
          label: `${customValueLabel} "${state.inputValue}"`,
          value: state.inputValue,
          isCustom: true,
        });
      }
    }

    return filtered;
  };

  // Default tag rendering
  const defaultRenderTags = (value: AutocompleteOption[], getTagProps: any) =>
    value.map((option, index) => (
      <Chip
        label={option.label}
        key={index}
        {...getTagProps({ index })}
        className={styles.chip}
        size={size}
      />
    ));

  // Custom option rendering
  const renderOption = (
    props: React.HTMLAttributes<HTMLLIElement>,
    option: AutocompleteOption
    // state: AutocompleteRenderOptionState
  ) => {
    const { key, ...otherProps } = props;
    return (
      <li key={key} {...otherProps} className={`${props.className} ${styles.option}`}>
        {option.isCustom ? (
          <span className={styles.customOption}>{option.label}</span>
        ) : (
          <span className={option.disabled ? styles.disabledOption : ''}>{option.label}</span>
        )}
      </li>
    );
  };
  // Custom Popper component
  const CustomPopper = (props: any) => (
    <Popper {...props} className={`${styles.popper} ${popperClassName}`} placement="bottom-start" />
  );

  return (
    <div className={`${styles.autocompleteWrapper} ${className}`}>
      <MuiAutocomplete
        {...autocompleteProps}
        multiple={multiple}
        options={providedOptions}
        loading={loading}
        filterOptions={customFilter || defaultFilterOptions}
        renderInput={params => (
          <TextField
            {...params}
            label={label}
            error={error}
            helperText={helperText}
            required={required}
            size={size}
            placeholder={placeholderText}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <React.Fragment>
                  {loading && (
                    <CircularProgress
                      color="inherit"
                      size={20}
                      className={styles.loadingIndicator}
                    />
                  )}
                  {params.InputProps.endAdornment}
                </React.Fragment>
              ),
            }}
            {...textFieldProps}
          />
        )}
        PopperComponent={CustomPopper}
        renderOption={renderOption}
        renderTags={renderTags || defaultRenderTags}
        groupBy={groupBy}
        getOptionLabel={option => option.label}
        getOptionDisabled={option => !!option.disabled}
        isOptionEqualToValue={(option, value) => option.value === value.value}
        classes={{
          ...autocompleteProps.classes,
          root: `${styles.autocomplete} ${size === 'small' ? styles.small : ''}` /* CHANGED: Added small class */,
        }}
      />
    </div>
  );
};

export default AtomAutocomplete;
